/**
 * Text helpers shared by the rules.
 *
 * Every function here is pure and total: given any string (including `''`) it
 * returns a sensible value and never throws. Rules rely on that so they can
 * stay free of defensive branching.
 */

/**
 * Invisible characters that get pasted in by accident — usually out of Word,
 * Google Docs, or a PDF export, which is this app's primary input path.
 *
 * They are split into two sets because they need opposite treatment:
 *
 * - `ZERO_WIDTH` occupies no space, so deleting it is correct.
 * - `SPACE_LIKE` reads as a space to a human. Deleting one would weld
 *   `'hello world'` into the single word `'helloworld'`, corrupting every word
 *   count and whole-word match downstream, so these collapse to a plain space.
 *
 * Both are built from explicit code points rather than a literal character
 * class: a literal would be invisible in an editor, unreviewable in a diff, and
 * silently corrupted by the first copy-paste. Listing numbers keeps it
 * inspectable.
 */
const ZERO_WIDTH_CODE_POINTS = [
  0x200b, // zero-width space
  0x200c, // zero-width non-joiner
  0x200d, // zero-width joiner
  0x200e, // left-to-right mark
  0x200f, // right-to-left mark
  0x202a, // left-to-right embedding
  0x202b, // right-to-left embedding
  0x202c, // pop directional formatting
  0x202d, // left-to-right override
  0x202e, // right-to-left override
  0x2060, // word joiner
  0xfeff, // byte-order mark
] as const;

const SPACE_LIKE_CODE_POINTS = [
  0x00a0, // non-breaking space
  0x2007, // figure space
  0x2009, // thin space
  0x202f, // narrow no-break space
] as const;

function charClass(codePoints: readonly number[]): RegExp {
  return new RegExp(`[${codePoints.map((cp) => String.fromCodePoint(cp)).join('')}]`, 'g');
}

const ZERO_WIDTH = charClass(ZERO_WIDTH_CODE_POINTS);
const SPACE_LIKE = charClass(SPACE_LIKE_CODE_POINTS);

/** Drops zero-width characters and turns exotic spaces into ordinary ones. */
function stripInvisibles(input: string): string {
  return input.replace(ZERO_WIDTH, '').replace(SPACE_LIKE, ' ');
}

/** Common bullet glyphs and list numbering at the start of a line. */
const LEADING_BULLET = /^\s*(?:[-*•·▪●○◦‣–—]+|\d+[.)])\s*/;

/** Matches a quantified claim: a digit, a percentage, or a currency amount. */
const METRIC = /\d|%|[$€£¥₹]/;

/** Escapes a string for literal use inside a RegExp. */
export function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Strips invisible characters, then trims and collapses whitespace runs. */
export function normalise(input: string): string {
  return stripInvisibles(input).replace(/\s+/g, ' ').trim();
}

/**
 * Character count as a user perceives it.
 *
 * `String.length` counts UTF-16 code units, so an emoji counts as 2 and a flag
 * as 4. `Array.from` counts code points, which is much closer to what
 * LinkedIn's own character counter shows.
 */
export function charCount(input: string): number {
  return Array.from(stripInvisibles(input).trim()).length;
}

export function words(input: string): string[] {
  const cleaned = normalise(input);
  return cleaned ? cleaned.split(' ') : [];
}

export function wordCount(input: string): number {
  return words(input).length;
}

/** Splits into sentences on `.`, `!`, or `?` followed by whitespace or the end. */
export function sentences(input: string): string[] {
  return normalise(input)
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Derives bullets from a pasted description.
 *
 * Splits on newlines and strips any bullet glyph or list numbering the user
 * copied along with the text. A description with no newlines yields a single
 * bullet, which is itself a signal the experience rules check for.
 */
export function toBullets(description: string): string[] {
  return stripInvisibles(description)
    .split(/\r?\n/)
    .map((line) => line.replace(LEADING_BULLET, '').trim())
    .filter(Boolean);
}

/** True when the text contains a number, percentage, or currency amount. */
export function hasMetric(input: string): boolean {
  return METRIC.test(input);
}

/** The first word, lowercased and stripped of punctuation. `''` when empty. */
export function firstWord(input: string): string {
  const [first = ''] = words(input);
  return first.toLowerCase().replace(/[^a-z'-]/g, '');
}

/**
 * Returns every phrase from `phrases` that appears in `haystack`.
 *
 * Matching is case-insensitive and whole-word, and treats whitespace inside a
 * phrase as flexible, so `'responsible for'` still matches `'Responsible  for'`
 * and text broken across a line break.
 */
export function findPhrases(haystack: string, phrases: readonly string[]): string[] {
  if (!haystack.trim()) return [];
  const found: string[] = [];
  for (const phrase of phrases) {
    const trimmed = phrase.trim();
    if (!trimmed) continue;
    const pattern = trimmed.split(/\s+/).map(escapeRegExp).join('\\s+');
    // `\b` is wrong at a non-word boundary (e.g. a phrase ending in '+' or
    // '/'), so only anchor the edges that actually start or end with a word
    // character. Without this, 'ci/cd' would never match.
    //
    // Anchors are derived from the *trimmed* phrase: judging them on the raw
    // string would drop the closing `\b` for an entry like `'i '`, and `\bi`
    // matches the start of "internship".
    const left = /^\w/.test(trimmed) ? '\\b' : '';
    const right = /\w$/.test(trimmed) ? '\\b' : '';
    if (new RegExp(`${left}${pattern}${right}`, 'i').test(haystack)) {
      found.push(phrase);
    }
  }
  return found;
}

/** True when the text has enough letters to judge and they are all uppercase. */
export function isShouting(input: string): boolean {
  const letters = input.replace(/[^a-zA-Z]/g, '');
  return letters.length >= 8 && letters === letters.toUpperCase();
}

/** Counts emoji and other pictographic characters. */
export function countEmoji(input: string): number {
  return (input.match(/\p{Extended_Pictographic}/gu) ?? []).length;
}

/** True when the text is broken into at least two paragraphs by a blank line. */
export function hasParagraphBreak(input: string): boolean {
  return /\n\s*\n/.test(input.trim());
}

/**
 * The leading `limit` characters, cut on a word boundary.
 * Used to inspect the part of a field LinkedIn shows before it truncates.
 */
export function leadingWindow(input: string, limit: number): string {
  const text = normalise(input);
  if (text.length <= limit) return text;
  const slice = text.slice(0, limit);
  const lastSpace = slice.lastIndexOf(' ');
  return lastSpace > limit * 0.5 ? slice.slice(0, lastSpace) : slice;
}

/** The trailing `limit` characters, cut on a word boundary. */
export function trailingWindow(input: string, limit: number): string {
  const text = normalise(input);
  if (text.length <= limit) return text;
  const slice = text.slice(-limit);
  const firstSpace = slice.indexOf(' ');
  return firstSpace >= 0 && firstSpace < limit * 0.5 ? slice.slice(firstSpace + 1) : slice;
}

/** Normalises and drops empties — the canonical form for a skills list. */
export function cleanList(values: readonly string[]): string[] {
  return values.map((v) => normalise(v)).filter(Boolean);
}

/**
 * Collapses a skill name for duplicate detection: lowercased with punctuation
 * and spacing removed, so `'Node.js'`, `'nodejs'`, and `'node JS'` all agree.
 */
export function canonicalSkill(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Ratio of `matched` to `total`, defined as 0 when `total` is 0. */
export function ratio(matched: number, total: number): number {
  return total === 0 ? 0 : matched / total;
}

/**
 * Linear score that is 1 at or above `good` and 0 at or below `bad`.
 * Works in both directions, so it also expresses "smaller is better".
 */
export function gradient(value: number, bad: number, good: number): number {
  if (bad === good) return value >= good ? 1 : 0;
  const t = (value - bad) / (good - bad);
  return Math.max(0, Math.min(1, t));
}
